import React, { useState } from 'react';
import { message, Spin } from 'antd';
import { ArchiveFilledIcon, ArchiveIcon, LockIcon, SecondNoteIcon, TestEyeIcon, TestStartIcon } from 'icons';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal } from 'modules/auth';
import { ITests, resetTestData, resetTestResults, setCurrentQuestionIndex } from 'modules/auto-test';
import { globalConst } from 'modules/global';
import { useSaveTest } from 'modules/saved-tests';

import { Visible } from 'components';

interface IProps {
  test?: ITests;
}

const TestCard: React.FC<IProps> = ({ test }) => {
  const t = useTranslations();
  const { push } = useRouter();
  const [isSavedState, setIsSavedState] = useState<boolean>(!!test?.is_saved);
  const user = useAppSelector(state => state.user);
  const dispatch = useAppDispatch();

  const saveTest = useSaveTest();

  const handleSaveTest = (newStatus: boolean) => {
    if (test?.test_id == null) return;
    if (user.phone) {
      saveTest.mutate(
        { id: test?.test_id, status: newStatus },
        {
          onSuccess: () => {
            setIsSavedState(newStatus);
            message.success(t('Muvaffaqiyatli_bajarildi'));
          }
        }
      );
    } else {
      dispatch(openAuthModal());
    }
  };

  const onClearTestData = () => {
    dispatch(resetTestData());
    dispatch(resetTestResults());
    dispatch(setCurrentQuestionIndex(0));
  };

  const handleCardClick = () => {
    if (test?.is_premium) return;
    onClearTestData();
    push(`${globalConst.pageNames.AUTO_TEST_PROCESS}/${test?.test_id}`);
  };

  return (
    <div className="relative">
      {test?.is_premium && (
        <Link
          href="/pro"
          className="absolute inset-0 z-10 flex cursor-pointer flex-col items-center justify-center rounded-2xl bg-white/70 backdrop-blur-[4px] transition hover:bg-white/80"
        >
          <LockIcon />
          <span className="text-xs text-gray-400">{t('akam_pro_ga_obuna_boling')}</span>
        </Link>
      )}

      <div
        onClick={handleCardClick}
        className={`relative z-0 flex h-full cursor-pointer flex-col space-y-7 rounded-2xl bg-white p-4 shadow-md transition-all duration-300 hover:shadow-lg ${
          test?.is_premium ? 'pointer-events-none select-none' : ''
        }`}
      >
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="rounded-md bg-gray-100 px-2 py-1 text-xs">Avto test</div>
            <div
              onClick={e => {
                e.stopPropagation();
                handleSaveTest(!isSavedState);
              }}
              className="cursor-pointer"
            >
              <Visible
                if={saveTest.isLoading}
                else={
                  <Visible if={isSavedState} else={<ArchiveIcon />}>
                    <ArchiveFilledIcon />
                  </Visible>
                }
              >
                <Spin size="small" />
              </Visible>
            </div>
          </div>
          <p className="mt-2 line-clamp-2 text-xs font-medium">{test?.lesson_name}</p>
        </div>

        <div className="flex items-end justify-between text-xs">
          <div className="flex items-center gap-1">
            <TestStartIcon />
            {test?.test_rating}
          </div>
          <div className="flex items-center gap-1">
            <SecondNoteIcon />
            {test?.test_count} {t('test')}
          </div>
          <div className="flex items-center gap-1">
            <TestEyeIcon />
            {test?.view_count}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCard;
